import React, { useState, useEffect, useCallback } from "react";
import { Form, Card, Modal, ProgressBar, Button, Row, Col, Table } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import * as Icons from "heroicons-react";
import { format } from "date-fns";
import axios from "axios";
import SideBar from "../Common/SideBar/SideBar";
import Header from "../Common/Header/Header";
import LoadingModal from "../Common/Modal/LoadingModal";
import { signInUser } from "../Utility/Auth";
import { notify } from "../Utility/Notify";
import { useSelector } from "react-redux";

const BackupRestore = () => {
  const auth = signInUser();
  const lang = useSelector((state) => state.languageReducer);
  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api/";

  const [backups, setBackups] = useState([]);
  const [settings, setSettings] = useState({
    autoBackup: false,
    frequency: "Day",
    time: "02:00",
    backupType: "Full",
    syncToDrive: false,
  });
  const [loader, setLoader] = useState(true);
  const handleLoaderClose = () => setLoader(false);

  const [activeTab, setActiveTab] = useState("All");
  
  // Drive Config states
  const [driveConfigs, setDriveConfigs] = useState([]);
  const [showDriveModal, setShowDriveModal] = useState(false);
  const [driveFormData, setDriveFormData] = useState({ _id: "", name: "", folderId: "", serviceAccountJson: "", isActive: false });
  
  // Telegram Config states
  const [telegramConfigs, setTelegramConfigs] = useState([]);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [telegramFormData, setTelegramFormData] = useState({ _id: "", name: "", botToken: "", chatId: "", isActive: false });

  // Restore Modal states
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restoreStatus, setRestoreStatus] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoader(true);
      const settingsRes = await axios.get(`${BASE_URL}backup/settings`);
      if (settingsRes.data) {
        setSettings({
          autoBackup: settingsRes.data.autoBackup || false,
          frequency: settingsRes.data.frequency || "Day",
          time: settingsRes.data.time || "02:00",
          backupType: settingsRes.data.backupType || "Full",
          syncToDrive: settingsRes.data.syncToDrive || false,
        });
      }

      const backupsRes = await axios.get(`${BASE_URL}backup`);
      if (backupsRes.data) setBackups(backupsRes.data);

      const driveConfigsRes = await axios.get(`${BASE_URL}backup/drive-configs`);
      if (driveConfigsRes.data) setDriveConfigs(driveConfigsRes.data);

      const telegramConfigsRes = await axios.get(`${BASE_URL}backup/telegram-configs`);
      if (telegramConfigsRes.data) setTelegramConfigs(telegramConfigsRes.data);
    } catch (error) {
      notify("Failed to fetch backup data", "error");
    } finally {
      setLoader(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveSettings = async () => {
    try {
      setLoader(true);
      await axios.post(`${BASE_URL}backup/settings`, settings);
      notify("Backup settings saved successfully!", "success");
      fetchData();
    } catch (error) {
      notify("Failed to save settings", "error");
    } finally {
      setLoader(false);
    }
  };

  const createBackup = async (type) => {
    try {
      setLoader(true);
      notify(`Creating ${type} backup. Please wait...`, "success");
      const res = await axios.post(`${BASE_URL}backup/run`, { type, syncToDrive: settings.syncToDrive });
      notify(res.data.message, "success");
      fetchData();
    } catch (error) {
      notify("Failed to create backup", "error");
    } finally {
      setLoader(false);
    }
  };

  const restoreBackup = async (id) => {
    if (!window.confirm("Restore Backup? Current data will be overwritten and this action cannot be undone.")) return;

    try {
      setShowRestoreModal(true);
      setRestoreProgress(0);
      setRestoreStatus("Preparing to restore...");

      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 15;
        if (progress > 90) progress = 90;
        setRestoreProgress(Math.floor(progress));
        if (progress < 30) setRestoreStatus("Extracting backup files...");
        else if (progress < 60) setRestoreStatus("Restoring database collections...");
        else if (progress < 85) setRestoreStatus("Applying file changes...");
        else setRestoreStatus("Finalizing restore...");
      }, 800);

      await axios.post(`${BASE_URL}backup/restore/${id}`);

      clearInterval(interval);
      setRestoreProgress(100);
      setRestoreStatus("Restore completed successfully! Logging out...");
      
      setTimeout(() => {
        setShowRestoreModal(false);
        localStorage.clear();
        window.location.href = "/login";
      }, 2000);

    } catch (error) {
      notify("Restore failed", "error");
      setShowRestoreModal(false);
    }
  };

  const deleteBackup = async (id) => {
    if (!window.confirm("Delete backup?")) return;
    try {
      setLoader(true);
      await axios.delete(`${BASE_URL}backup/${id}`);
      notify("Backup deleted successfully!", "success");
      fetchData();
    } catch (error) {
      notify("Failed to delete backup", "error");
    } finally {
      setLoader(false);
    }
  };

  const downloadBackup = (name) => {
    window.open(`${BASE_URL}backup/download/${name}`, '_blank');
  };

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    if (!file.name.endsWith('.zip')) {
      notify("Only ZIP files are allowed!", "error");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoader(true);
      notify("Uploading backup file. Please wait...", "success");
      await axios.post(`${BASE_URL}backup/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      notify("Backup uploaded successfully!", "success");
      fetchData();
    } catch (error) {
      notify("Failed to upload backup", "error");
    } finally {
      setLoader(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: false });

  // Drive Actions
  const handleDriveSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoader(true);
      if (driveFormData._id) {
        await axios.put(`${BASE_URL}backup/drive-configs/${driveFormData._id}`, driveFormData);
        notify("Drive config updated successfully!", "success");
      } else {
        await axios.post(`${BASE_URL}backup/drive-configs`, driveFormData);
        notify("Drive config added successfully!", "success");
      }
      setShowDriveModal(false);
      setDriveFormData({ _id: "", name: "", folderId: "", serviceAccountJson: "", isActive: false });
      fetchData();
    } catch (err) {
      notify("Failed to save Drive config", "error");
    } finally {
      setLoader(false);
    }
  };

  const handleDriveDelete = async (id) => {
    if (!window.confirm("Delete Drive config?")) return;
    try {
      setLoader(true);
      await axios.delete(`${BASE_URL}backup/drive-configs/${id}`);
      notify("Drive config deleted!", "success");
      fetchData();
    } catch (err) {
      notify("Failed to delete config", "error");
    } finally {
      setLoader(false);
    }
  };

  // Telegram Actions
  const handleTelegramSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoader(true);
      if (telegramFormData._id) {
        await axios.put(`${BASE_URL}backup/telegram-configs/${telegramFormData._id}`, telegramFormData);
        notify("Telegram config updated successfully!", "success");
      } else {
        await axios.post(`${BASE_URL}backup/telegram-configs`, telegramFormData);
        notify("Telegram config added successfully!", "success");
      }
      setShowTelegramModal(false);
      setTelegramFormData({ _id: "", name: "", botToken: "", chatId: "", isActive: false });
      fetchData();
    } catch (err) {
      notify("Failed to save Telegram config", "error");
    } finally {
      setLoader(false);
    }
  };

  const handleTelegramDelete = async (id) => {
    if (!window.confirm("Delete Telegram config?")) return;
    try {
      setLoader(true);
      await axios.delete(`${BASE_URL}backup/telegram-configs/${id}`);
      notify("Telegram config deleted!", "success");
      fetchData();
    } catch (err) {
      notify("Failed to delete config", "error");
    } finally {
      setLoader(false);
    }
  };

  // Tab Filtering
  const filteredBackups = backups.filter(backup => {
    if (activeTab === "All") return true;
    return backup.type === activeTab;
  });

  return (
    <div>
      <LoadingModal
        title={"Please Wait"}
        onShow={loader}
        handleClose={handleLoaderClose}
      ></LoadingModal>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-2">
            <SideBar></SideBar>
          </div>
          <div className="col-md-10">
            <Header title={lang?.backup || "Backup & Restore"}></Header>
            <p className="text-muted mt-1">Manage backups and restore your data</p>

            {/* 1. Scheduler Panel */}
            <Card className="shadow-sm border-0 mb-4 bg-white">
              <Card.Body className="p-4">
                <h5 className="mb-0 fw-bold">Automatic Backup Schedule</h5>
                <p className="text-muted small mb-4">Configure automatic backups to protect your data.</p>
                
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <Form.Check 
                      type="switch"
                      id="autoBackup-switch"
                      label="Auto Backup"
                      name="autoBackup"
                      checked={settings.autoBackup}
                      onChange={handleSettingsChange}
                      className="fw-bold fs-6 me-2 align-self-center"
                    />
                    
                    {settings.autoBackup && (
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="text-secondary align-self-center">Every</span>
                        <Form.Select 
                          name="frequency" 
                          value={settings.frequency} 
                          onChange={handleSettingsChange}
                          style={{ borderRadius: "6px", width: "auto", display: "inline-block" }}
                          className="py-1 px-3"
                        >
                          <option value="Day">Day</option>
                          <option value="Week">Week</option>
                          <option value="Month">Month</option>
                        </Form.Select>

                        <span className="text-secondary align-self-center">at</span>
                        <Form.Control 
                          type="time" 
                          name="time" 
                          value={settings.time} 
                          onChange={handleSettingsChange}
                          style={{ borderRadius: "6px", width: "auto", display: "inline-block" }}
                          className="py-1 px-2"
                        />

                        <span className="text-secondary align-self-center">taking</span>
                        <Form.Select 
                          name="backupType" 
                          value={settings.backupType} 
                          onChange={handleSettingsChange}
                          style={{ borderRadius: "6px", width: "auto", display: "inline-block" }}
                          className="py-1 px-3"
                        >
                          <option value="Full">Full</option>
                          <option value="Database">Database</option>
                          <option value="Files">Files</option>
                        </Form.Select>
                        <span className="text-secondary align-self-center">backup</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="d-flex align-items-center gap-4">
                    <Form.Check 
                      type="switch"
                      id="syncToDrive-switch"
                      label="Sync to Drive"
                      name="syncToDrive"
                      checked={settings.syncToDrive}
                      onChange={handleSettingsChange}
                      className="fw-bold fs-6 align-self-center"
                    />
                    <Button 
                      style={{ backgroundColor: "#F2652D", borderColor: "#F2652D", borderRadius: "6px" }}
                      className="fw-bold px-4 py-2 text-white border-0" 
                      onClick={saveSettings}
                    >
                      Save Settings
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* 2. Manual Backup Trigger Cards */}
            <h5 className="fw-bold mb-3">Create New Backup</h5>
            <Row className="mb-4 g-3">
              <Col md={4}>
                <Card 
                  className="shadow-sm border border-light text-center p-3 h-100 hover-shadow cursor-pointer"
                  onClick={() => createBackup("Database")}
                  style={{ cursor: "pointer", borderRadius: "10px" }}
                >
                  <Card.Body>
                    <Icons.DatabaseOutline className="mx-auto text-dark mb-2" size={32} />
                    <h6 className="fw-bold mb-1">Database</h6>
                    <span className="text-muted small">Backup database only</span>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card 
                  className="shadow-sm border border-light text-center p-3 h-100 hover-shadow cursor-pointer"
                  onClick={() => createBackup("Files")}
                  style={{ cursor: "pointer", borderRadius: "10px" }}
                >
                  <Card.Body>
                    <Icons.FolderOutline className="mx-auto text-dark mb-2" size={32} />
                    <h6 className="fw-bold mb-1">Files</h6>
                    <span className="text-muted small">Backup files only</span>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card 
                  className="shadow-sm border border-light text-center p-3 h-100 hover-shadow cursor-pointer"
                  onClick={() => createBackup("Full")}
                  style={{ cursor: "pointer", borderRadius: "10px" }}
                >
                  <Card.Body>
                    <Icons.ArchiveOutline className="mx-auto text-dark mb-2" size={32} />
                    <h6 className="fw-bold mb-1">Full Backup</h6>
                    <span className="text-muted small">Database + Files</span>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* 3. Drag & Drop File Upload */}
            <h5 className="fw-bold mb-3">Upload Backup</h5>
            <div 
              {...getRootProps()} 
              className={`border border-2 text-center p-5 rounded-3 mb-4 d-flex flex-column align-items-center justify-content-center bg-white shadow-sm cursor-pointer ${
                isDragActive ? 'border-primary bg-light' : 'border-dashed'
              }`}
              style={{ borderStyle: "dashed", cursor: "pointer" }}
            >
              <input {...getInputProps()} />
              <Icons.UploadOutline className="text-muted mb-2" size={40} />
              <h6 className="fw-bold mb-1">Drag and drop your backup file here, or click to browse</h6>
              <span className="text-muted small">Only ZIP files up to 2GB are accepted</span>
            </div>

            {/* 4. Tab List Navigation */}
            <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
              <h5 className="fw-bold m-0">Available Backups</h5>
              <span className="text-muted small">{backups.length} backups available</span>
            </div>

            <div className="d-flex w-100 gap-2 mb-4 bg-light p-2 rounded-3 align-items-center justify-content-between">
              {["All", "Database", "Files", "Full", "Drive Settings", "Telegram Settings"].map((tab) => {
                let label = tab;
                if (tab === "All") label = `All (${backups.length})`;
                if (tab === "Database") label = `Database (${backups.filter(b => b.type === "Database").length})`;
                if (tab === "Files") label = `Files (${backups.filter(b => b.type === "Files").length})`;
                if (tab === "Full") label = `Full (${backups.filter(b => b.type === "Full").length})`;

                return (
                  <Button 
                    key={tab}
                    variant={activeTab === tab ? "dark" : "light"}
                    onClick={() => setActiveTab(tab)}
                    className="flex-fill rounded-pill py-2 font-weight-bold"
                    style={{ fontSize: "0.9rem" }}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>

            {/* Tab Contents */}
            {activeTab !== "Drive Settings" && activeTab !== "Telegram Settings" && (
              <div className="d-flex flex-column gap-2 mb-5">
                {filteredBackups.length > 0 ? (
                  filteredBackups.map((backup) => (
                    <div key={backup._id} className="d-flex align-items-center justify-content-between p-3 bg-white rounded-3 shadow-sm border border-light">
                      <div className="d-flex align-items-center gap-3">
                        <div className="p-2 bg-light rounded-3">
                          <Icons.DocumentTextOutline className="text-secondary" size={24} />
                        </div>
                        <div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold text-dark">{backup.name}</span>
                            <span className="badge rounded-pill bg-dark text-white px-2.5 py-1" style={{ fontSize: "0.75rem" }}>
                              {backup.status}
                            </span>
                            <span className="badge rounded-pill bg-light text-dark border px-2.5 py-1" style={{ fontSize: "0.75rem" }}>
                              {backup.type}
                            </span>
                          </div>
                          <span className="text-muted small">{backup.size} - {format(new Date(backup.createdAt), "MM/dd/yyyy, hh:mm a")}</span>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <Button variant="outline-dark" size="sm" onClick={() => downloadBackup(backup.name)}>
                          <Icons.DownloadOutline size={18} />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => restoreBackup(backup._id)}>
                          <Icons.RefreshOutline size={18} />
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => deleteBackup(backup._id)}>
                          <Icons.TrashOutline size={18} />
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center p-4 bg-white border rounded-3 text-muted">No backups found under this category</div>
                )}
              </div>
            )}

            {/* Google Drive Configurations Tab */}
            {activeTab === "Drive Settings" && (
              <Card className="shadow-sm border-0 mb-5">
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">Google Drive Accounts</h6>
                    <Button 
                      variant="dark" 
                      size="sm" 
                      onClick={() => {
                        setDriveFormData({ _id: "", name: "", folderId: "", serviceAccountJson: "", isActive: false });
                        setShowDriveModal(true);
                      }}
                    >
                      <Icons.Plus size={18} /> Add Drive Account
                    </Button>
                  </div>
                  <Table responsive hover className="align-middle">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Folder ID</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driveConfigs.map((config) => (
                        <tr key={config._id}>
                          <td>{config.name}</td>
                          <td><code>{config.folderId}</code></td>
                          <td>
                            <Form.Check 
                              type="switch"
                              id={`drive-active-${config._id}`}
                              checked={config.isActive}
                              onChange={async (e) => {
                                setLoader(true);
                                try {
                                  await axios.put(`${BASE_URL}backup/drive-configs/${config._id}`, { isActive: e.target.checked });
                                  notify("Drive active status updated!", "success");
                                  fetchData();
                                } catch (err) {
                                  notify("Failed to update status", "error");
                                } finally {
                                  setLoader(false);
                                }
                              }}
                            />
                          </td>
                          <td>
                            <Button 
                              variant="outline-dark" 
                              size="sm" 
                              className="me-2"
                              onClick={() => {
                                setDriveFormData(config);
                                setShowDriveModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDriveDelete(config._id)}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Telegram Configurations Tab */}
            {activeTab === "Telegram Settings" && (
              <Card className="shadow-sm border-0 mb-5">
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">Telegram Integration Configurations</h6>
                    <Button 
                      variant="dark" 
                      size="sm" 
                      onClick={() => {
                        setTelegramFormData({ _id: "", name: "", botToken: "", chatId: "", isActive: false });
                        setShowTelegramModal(true);
                      }}
                    >
                      <Icons.Plus size={18} /> Add Telegram config
                    </Button>
                  </div>
                  <Table responsive hover className="align-middle">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Chat ID</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {telegramConfigs.map((config) => (
                        <tr key={config._id}>
                          <td>{config.name}</td>
                          <td><code>{config.chatId}</code></td>
                          <td>
                            <Form.Check 
                              type="switch"
                              id={`tele-active-${config._id}`}
                              checked={config.isActive}
                              onChange={async (e) => {
                                setLoader(true);
                                try {
                                  await axios.put(`${BASE_URL}backup/telegram-configs/${config._id}`, { isActive: e.target.checked });
                                  notify("Telegram active status updated!", "success");
                                  fetchData();
                                } catch (err) {
                                  notify("Failed to update status", "error");
                                } finally {
                                  setLoader(false);
                                }
                              }}
                            />
                          </td>
                          <td>
                            <Button 
                              variant="outline-dark" 
                              size="sm" 
                              className="me-2"
                              onClick={() => {
                                setTelegramFormData(config);
                                setShowTelegramModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleTelegramDelete(config._id)}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

          </div>
        </div>
      </div>

      {/* 5. Restoration Progress Modal */}
      <Modal show={showRestoreModal} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center p-4">
          <h5 className="fw-bold">System Restoration In Progress</h5>
          <ProgressBar animated now={restoreProgress} label={`${restoreProgress}%`} className="my-3" variant="danger" />
          <p className="text-muted m-0">{restoreStatus}</p>
        </Modal.Body>
      </Modal>

      {/* Google Drive Account Add/Edit Modal */}
      <Modal show={showDriveModal} onHide={() => setShowDriveModal(false)} centered>
        <Form onSubmit={handleDriveSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">{driveFormData._id ? "Edit Google Drive" : "Add Google Drive Account"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Profile Name</Form.Label>
              <Form.Control 
                type="text" 
                required
                value={driveFormData.name}
                onChange={(e) => setDriveFormData({ ...driveFormData, name: e.target.value })}
                placeholder="Google Drive Account"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Folder ID</Form.Label>
              <Form.Control 
                type="text" 
                required
                value={driveFormData.folderId}
                onChange={(e) => setDriveFormData({ ...driveFormData, folderId: e.target.value })}
                placeholder="Google Drive Folder ID"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Credentials JSON</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={5}
                required
                value={driveFormData.serviceAccountJson}
                onChange={(e) => setDriveFormData({ ...driveFormData, serviceAccountJson: e.target.value })}
                placeholder="Paste the service account keys JSON content here"
                style={{ fontSize: "0.85rem", fontFamily: "monospace" }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setShowDriveModal(false)}>Cancel</Button>
            <Button variant="dark" type="submit">Save Configuration</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Telegram Config Add/Edit Modal */}
      <Modal show={showTelegramModal} onHide={() => setShowTelegramModal(false)} centered>
        <Form onSubmit={handleTelegramSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">{telegramFormData._id ? "Edit Telegram config" : "Add Telegram Integration"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Configuration Name</Form.Label>
              <Form.Control 
                type="text" 
                required
                value={telegramFormData.name}
                onChange={(e) => setTelegramFormData({ ...telegramFormData, name: e.target.value })}
                placeholder="Telegram Alert Channel"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Bot Token</Form.Label>
              <Form.Control 
                type="text" 
                required
                value={telegramFormData.botToken}
                onChange={(e) => setTelegramFormData({ ...telegramFormData, botToken: e.target.value })}
                placeholder="Telegram Bot Token"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Chat ID</Form.Label>
              <Form.Control 
                type="text" 
                required
                value={telegramFormData.chatId}
                onChange={(e) => setTelegramFormData({ ...telegramFormData, chatId: e.target.value })}
                placeholder="Telegram Chat ID or Group ID"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setShowTelegramModal(false)}>Cancel</Button>
            <Button variant="dark" type="submit">Save Configuration</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BackupRestore;
